/**
 * PremiumCommandBar.jsx
 * Command Bar premium inspirado no Linear App (⌘K)
 *
 * Features:
 * - Atalho global: Ctrl+K (Windows/Linux) ou ⌘K (Mac)
 * - Busca em tempo real com debounce
 * - Categorias: Recentes, Veículos, Motoristas, Geofences, Ações Rápidas
 * - Navegação por teclado (↑↓ Enter Esc Tab)
 * - Highlight do termo buscado
 * - Animações suaves
 * - Dark/Light mode via tokens
 *
 * Uso:
 *   const { open, setOpen } = useCommandBar();
 *   <PremiumCommandBar
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     vehicles={devices}
 *     drivers={drivers}
 *     geofences={geofences}
 *     onSelectVehicle={(v) => navigate(`/device/${v.id}`)}
 *   />
 */

import React, {
 useState, useEffect, useCallback, useMemo, useRef 
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Box,
  InputBase,
  Typography,
  Fade,
  Divider,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import MapIcon from '@mui/icons-material/Map';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FenceIcon from '@mui/icons-material/Fence';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

// ============================================================================
// CONSTANTES
// ============================================================================

const DEBOUNCE_MS = 150;
const MAX_RESULTS_PER_CATEGORY = 5;
const MAX_RECENT = 5;

// Storage key para recentes
const RECENT_STORAGE_KEY = 'commandbar_recent';

// Categorias de resultados
const CATEGORIES = [
  { id: 'recent', label: 'Recentes', icon: <HistoryIcon fontSize="small" /> },
  { id: 'vehicles', label: 'Veículos', icon: <DirectionsCarIcon fontSize="small" /> },
  { id: 'drivers', label: 'Motoristas', icon: <PersonIcon fontSize="small" /> },
  { id: 'geofences', label: 'Geofences', icon: <LocationOnIcon fontSize="small" /> },
  { id: 'actions', label: 'Ações Rápidas', icon: <FlashOnIcon fontSize="small" /> },
];

// ============================================================================
// HOOK: useCommandBar
// ============================================================================

export const useCommandBar = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K ou ⌘K para abrir
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      // Esc para fechar
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return {
    open,
    setOpen,
    toggle: () => setOpen((o) => !o),
    close: () => setOpen(false),
  };
};

// ============================================================================
// UTILITÁRIOS
// ============================================================================

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Highlight text
const HighlightText = ({ text, highlight, tokens }) => {
  if (!highlight || !text) return text;

  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <Box
            key={i}
            component="span"
            sx={{
              backgroundColor: tokens.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
              color: tokens.accent?.primary || '#00d4aa',
              borderRadius: '2px',
              px: 0.25,
            }}
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </>
  );
};

// Get status color
const getStatusColor = (status, tokens) => {
  const defaultColors = {
    online: '#2ea043',
    moving: '#0d6efd',
    idle: '#f0ad4e',
    stopped: '#8b949e',
    offline: '#dc3545',
    alarm: '#dc3545',
    unknown: '#484f58',
  };
  const statusMap = {
    online: tokens.status?.online || defaultColors.online,
    moving: tokens.status?.moving || defaultColors.moving,
    idle: tokens.status?.idle || defaultColors.idle,
    stopped: tokens.status?.stopped || defaultColors.stopped,
    offline: tokens.status?.offline || defaultColors.offline,
    alarm: tokens.status?.alarm || defaultColors.alarm,
  };
  return statusMap[status] || tokens.status?.unknown || defaultColors.unknown;
};

// Status indicator
const StatusDot = ({ status, tokens }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: getStatusColor(status, tokens),
      flexShrink: 0,
    }}
  />
);

// Keyboard shortcut badge
const ShortcutBadge = ({ shortcut, tokens }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 0.5,
    }}
  >
    {shortcut.split(' ').map((key, i) => (
      <Box
        key={i}
        sx={{
          px: 1,
          py: 0.25,
          backgroundColor: tokens.commandBar?.shortcutBg || 'rgba(255, 255, 255, 0.1)',
          border: `1px solid ${tokens.commandBar?.shortcutBorder || 'rgba(255, 255, 255, 0.1)'}`,
          borderRadius: tokens.radius?.sm || '4px',
          fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
          fontFamily: tokens.typography?.fontFamily?.mono || 'monospace',
          color: tokens.commandBar?.shortcutText || '#8b949e',
          lineHeight: 1.4,
        }}
      >
        {key}
      </Box>
    ))}
  </Box>
);

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const PremiumCommandBar = ({
  open,
  onClose,

  // Dados para busca
  vehicles = [],
  drivers = [],
  geofences = [],

  // Callbacks de seleção
  onSelectVehicle,
  onSelectDriver,
  onSelectGeofence,

  // Customização
  placeholder = 'Buscar veículos, motoristas, comandos...',
  maxResults = MAX_RESULTS_PER_CATEGORY,
  showRecent = true,
}) => {
  const tokens = usePremiumTokens();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Estado
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentItems, setRecentItems] = useState([]);

  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);

  // Blur active element when modal opens to prevent aria-hidden conflict
  useEffect(() => {
    if (open && document.activeElement) {
      document.activeElement.blur();
    }
  }, [open]);

  // Carregar recentes do localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_STORAGE_KEY);
      if (stored) {
        setRecentItems(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error loading recent items:', e);
    }
  }, []);

  // Salvar item nos recentes
  const addToRecent = useCallback((item) => {
    setRecentItems((prev) => {
      const filtered = prev.filter((r) => !(r.id === item.id && r.type === item.type));
      const updated = [{ ...item, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving recent items:', e);
      }
      return updated;
    });
  }, []);

  // Ações rápidas
  const quickActions = useMemo(
    () => [
      {
        id: 'goto-map',
        label: 'Ir para Mapa',
        shortcut: 'G M',
        icon: <MapIcon fontSize="small" />,
        action: () => navigate('/'),
      },
      {
        id: 'goto-vehicles',
        label: 'Ir para Veículos',
        shortcut: 'G V',
        icon: <DirectionsCarIcon fontSize="small" />,
        action: () => navigate('/settings/devices'),
      },
      {
        id: 'goto-drivers',
        label: 'Ir para Motoristas',
        shortcut: 'G D',
        icon: <PersonIcon fontSize="small" />,
        action: () => navigate('/settings/drivers'),
      },
      {
        id: 'goto-geofences',
        label: 'Ir para Geocercas',
        shortcut: 'G G',
        icon: <FenceIcon fontSize="small" />,
        action: () => navigate('/geofences'),
      },
      {
        id: 'goto-reports',
        label: 'Ir para Relatórios',
        shortcut: 'G R',
        icon: <AssessmentIcon fontSize="small" />,
        action: () => navigate('/reports/combined'),
      },
      {
        id: 'goto-settings',
        label: 'Configurações',
        shortcut: 'G S',
        icon: <SettingsIcon fontSize="small" />,
        action: () => navigate('/settings/preferences'),
      },
      {
        id: 'goto-users',
        label: 'Ir para Usuários',
        shortcut: 'G U',
        icon: <PeopleIcon fontSize="small" />,
        action: () => navigate('/settings/users'),
      },
      {
        id: 'goto-notifications',
        label: 'Ir para Notificações',
        shortcut: 'G N',
        icon: <NotificationsIcon fontSize="small" />,
        action: () => navigate('/settings/notifications'),
      },
      {
        id: 'goto-fleet',
        label: 'Ir para Fleet',
        shortcut: 'G F',
        icon: <DashboardIcon fontSize="small" />,
        action: () => navigate('/fleet'),
      },
      {
        id: 'new-device',
        label: 'Novo Veículo',
        shortcut: 'N',
        icon: <AddIcon fontSize="small" />,
        action: () => navigate('/settings/device'),
      },
    ],
    [navigate]
  );

  // Filtrar resultados
  const filteredResults = useMemo(() => {
    const results = {
      recent: [],
      vehicles: [],
      drivers: [],
      geofences: [],
      actions: [],
    };

    const searchLower = debouncedQuery.toLowerCase().trim();

    // Recentes (apenas se não há busca)
    if (showRecent && !searchLower) {
      results.recent = recentItems.slice(0, maxResults);
    }

    // Veículos
    if (vehicles.length > 0) {
      const filtered = vehicles
        .filter((v) => {
          if (!searchLower) return true;
          const name = (v.name || '').toLowerCase();
          const plate = (v.attributes?.form?.plate || '').toLowerCase();
          const uniqueId = (v.uniqueId || '').toLowerCase();
          return name.includes(searchLower) || plate.includes(searchLower) || uniqueId.includes(searchLower);
        })
        .slice(0, maxResults)
        .map((v) => ({
          id: v.id,
          type: 'vehicle',
          name: v.name,
          subtitle: v.attributes?.form?.plate || v.uniqueId,
          status: v.status,
          data: v,
        }));
      results.vehicles = filtered;
    }

    // Motoristas
    if (drivers.length > 0) {
      const filtered = drivers
        .filter((d) => {
          if (!searchLower) return true;
          return (d.name || '').toLowerCase().includes(searchLower);
        })
        .slice(0, maxResults)
        .map((d) => ({
          id: d.id,
          type: 'driver',
          name: d.name,
          subtitle: d.attributes?.vehicle ? `Veículo: ${d.attributes.vehicle}` : '',
          data: d,
        }));
      results.drivers = filtered;
    }

    // Geofences
    if (geofences.length > 0) {
      const filtered = geofences
        .filter((g) => {
          if (!searchLower) return true;
          return (g.name || '').toLowerCase().includes(searchLower);
        })
        .slice(0, maxResults)
        .map((g) => ({
          id: g.id,
          type: 'geofence',
          name: g.name,
          subtitle: g.description || '',
          data: g,
        }));
      results.geofences = filtered;
    }

    // Ações rápidas
    const filteredActions = quickActions
      .filter((a) => {
        if (!searchLower) return true;
        return a.label.toLowerCase().includes(searchLower);
      })
      .slice(0, maxResults)
      .map((a) => ({
        id: a.id,
        type: 'action',
        name: a.label,
        shortcut: a.shortcut,
        icon: a.icon,
        action: a.action,
      }));
    results.actions = filteredActions;

    return results;
  }, [debouncedQuery, vehicles, drivers, geofences, quickActions, recentItems, showRecent, maxResults]);

  // Flatten para navegação
  const flattenedResults = useMemo(() => {
    const flat = [];
    CATEGORIES.forEach((cat) => {
      const items = filteredResults[cat.id] || [];
      items.forEach((item) => {
        flat.push({ ...item, category: cat.id });
      });
    });
    return flat;
  }, [filteredResults]);

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Scroll para item selecionado
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  // Handler de seleção
  const handleSelect = useCallback(
    (item) => {
      if (!item) return;

      if (item.type === 'action') {
        item.action?.();
        onClose();
        return;
      }

      // Adicionar aos recentes
      addToRecent({
        id: item.id,
        type: item.type,
        name: item.name,
        subtitle: item.subtitle,
      });

      // Callback apropriado
      switch (item.type) {
        case 'vehicle':
          if (onSelectVehicle) {
            onSelectVehicle(item.data);
          } else {
            navigate(`/settings/device/${item.id}`);
          }
          break;
        case 'driver':
          if (onSelectDriver) {
            onSelectDriver(item.data);
          } else {
            navigate(`/settings/driver/${item.id}`);
          }
          break;
        case 'geofence':
          if (onSelectGeofence) {
            onSelectGeofence(item.data);
          } else {
            navigate(`/settings/geofence/${item.id}`);
          }
          break;
        default:
          break;
      }

      onClose();
    },
    [onSelectVehicle, onSelectDriver, onSelectGeofence, onClose, navigate, addToRecent]
  );

  // Navegação por teclado
  const handleKeyDown = useCallback(
    (e) => {
      const total = flattenedResults.length;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % total);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + total) % total);
          break;
        case 'Enter':
          e.preventDefault();
          handleSelect(flattenedResults[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'Tab':
          e.preventDefault();
          // Pular para próxima categoria
          const currentCat = flattenedResults[selectedIndex]?.category;
          let nextIndex = selectedIndex + 1;
          while (nextIndex < total && flattenedResults[nextIndex]?.category === currentCat) {
            nextIndex++;
          }
          setSelectedIndex(nextIndex < total ? nextIndex : 0);
          break;
        default:
          break;
      }
    },
    [flattenedResults, selectedIndex, handleSelect, onClose]
  );

  // ============================================================================
  // ESTILOS
  // ============================================================================

  const modalSx = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    pt: { xs: 0, sm: '10vh' },
  };

  const backdropSx = {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  };

  const containerSx = {
    width: '100%',
    maxWidth: { xs: '100%', sm: 640 },
    maxHeight: { xs: '100vh', sm: '70vh' },
    backgroundColor: tokens.commandBar?.bg || '#161b22',
    borderRadius: { xs: 0, sm: tokens.radius?.xl || '16px' },
    border: `1px solid ${tokens.commandBar?.border || 'rgba(255, 255, 255, 0.1)'}`,
    boxShadow: tokens.commandBar?.shadow || '0 16px 70px rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
    // Animation
    animation: 'commandBarScaleIn 0.15s ease-out',
    '@keyframes commandBarScaleIn': {
      from: {
        opacity: 0,
        transform: 'scale(0.95)',
      },
      to: {
        opacity: 1,
        transform: 'scale(1)',
      },
    },
  };

  const inputContainerSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1.5,
    borderBottom: `1px solid ${tokens.commandBar?.border || 'rgba(255, 255, 255, 0.1)'}`,
  };

  const resultsSx = {
    flex: 1,
    overflow: 'auto',
    py: 1,
  };

  const categoryHeaderSx = {
    px: 2,
    py: 0.75,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: tokens.commandBar?.categoryText || '#6e7681',
    fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
    fontWeight: tokens.typography?.fontWeight?.semibold || 600,
    textTransform: 'uppercase',
    letterSpacing: tokens.typography?.letterSpacing?.wider || '0.05em',
  };

  const resultItemSx = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1,
    cursor: 'pointer',
    backgroundColor: isSelected
      ? (tokens.commandBar?.resultBgSelected || 'rgba(0, 212, 170, 0.1)')
      : (tokens.commandBar?.resultBg || 'transparent'),
    transition: tokens.transitions?.colors || 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: isSelected
        ? (tokens.commandBar?.resultBgSelected || 'rgba(0, 212, 170, 0.1)')
        : (tokens.commandBar?.resultBgHover || 'rgba(255, 255, 255, 0.06)'),
    },
  });

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderCategory = (category, items, startIndex) => {
    if (items.length === 0) return null;

    return (
      <Box key={category.id}>
        <Box sx={categoryHeaderSx}>
          {category.icon}
          <span>{category.label}</span>
        </Box>
        {items.map((item, idx) => {
          const globalIndex = startIndex + idx;
          const isSelected = selectedIndex === globalIndex;

          return (
            <Box
              key={`${item.type}-${item.id}`}
              data-index={globalIndex}
              sx={resultItemSx(isSelected)}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
            >
              {/* Icon/Status */}
              {item.type === 'vehicle' && <StatusDot status={item.status} tokens={tokens} />}
              {item.type === 'driver' && <PersonIcon fontSize="small" sx={{ color: tokens.text?.tertiary || '#6e7681' }} />}
              {item.type === 'geofence' && <LocationOnIcon fontSize="small" sx={{ color: tokens.text?.tertiary || '#6e7681' }} />}
              {item.type === 'action' && item.icon}
              {item.type === 'recent' && (
                item.originalType === 'vehicle' ? (
                  <DirectionsCarIcon fontSize="small" sx={{ color: tokens.text?.tertiary || '#6e7681' }} />
                ) : item.originalType === 'driver' ? (
                  <PersonIcon fontSize="small" sx={{ color: tokens.text?.tertiary || '#6e7681' }} />
                ) : (
                  <HistoryIcon fontSize="small" sx={{ color: tokens.text?.tertiary || '#6e7681' }} />
                )
              )}

              {/* Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: tokens.commandBar?.resultText || '#f0f6fc',
                    fontSize: tokens.typography?.fontSize?.md || '0.875rem',
                    fontWeight: tokens.typography?.fontWeight?.medium || 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <HighlightText text={item.name} highlight={debouncedQuery} tokens={tokens} />
                </Typography>
                {item.subtitle && (
                  <Typography
                    sx={{
                      color: tokens.commandBar?.resultTextSecondary || '#8b949e',
                      fontSize: tokens.typography?.fontSize?.sm || '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                )}
              </Box>

              {/* Shortcut or Enter hint */}
              {item.shortcut ? (
                <ShortcutBadge shortcut={item.shortcut} tokens={tokens} />
              ) : isSelected ? (
                <KeyboardReturnIcon fontSize="small" sx={{ color: tokens.text?.tertiary || '#6e7681' }} />
              ) : null}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Calcular índices iniciais de cada categoria
  let currentIndex = 0;
  const categoryStartIndices = {};
  CATEGORIES.forEach((cat) => {
    categoryStartIndices[cat.id] = currentIndex;
    currentIndex += (filteredResults[cat.id] || []).length;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      sx={modalSx}
      slotProps={{
        backdrop: {
          sx: backdropSx,
        },
      }}
    >
      <Fade in={open}>
        <Box sx={containerSx} onKeyDown={handleKeyDown} tabIndex={-1}>
          {/* Input */}
          <Box sx={inputContainerSx}>
            <SearchIcon sx={{ color: tokens.text?.tertiary || '#6e7681', fontSize: 20 }} />
            <InputBase
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder={placeholder}
              fullWidth
              sx={{
                flex: 1,
                fontSize: tokens.typography?.fontSize?.lg || '1rem',
                color: tokens.commandBar?.inputText || '#f0f6fc',
              }}
              inputProps={{
                style: {
                  color: tokens.commandBar?.inputText || '#f0f6fc',
                },
              }}
            />
            <ShortcutBadge shortcut="⌘K" tokens={tokens} />
            <IconButton size="small" onClick={onClose} sx={{ color: tokens.text?.tertiary || '#6e7681' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Results */}
          <Box ref={listRef} sx={resultsSx}>
            {flattenedResults.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: tokens.text?.muted || '#6e7681' }}>
                  {query ? 'Nenhum resultado encontrado' : 'Comece a digitar para buscar...'}
                </Typography>
              </Box>
            ) : (
              CATEGORIES.map((cat) =>
                renderCategory(cat, filteredResults[cat.id] || [], categoryStartIndices[cat.id])
              )
            )}
          </Box>

          {/* Footer hint */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: `1px solid ${tokens.commandBar?.border || 'rgba(255, 255, 255, 0.1)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: tokens.text?.muted || '#6e7681',
              fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ShortcutBadge shortcut="↑↓" tokens={tokens} />
              <span>navegar</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ShortcutBadge shortcut="↵" tokens={tokens} />
              <span>selecionar</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ShortcutBadge shortcut="esc" tokens={tokens} />
              <span>fechar</span>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

PremiumCommandBar.displayName = 'PremiumCommandBar';

export default PremiumCommandBar;
